import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { openChatWithMessage } from "@/lib/channel";

// 카드결제 베타 초대(토스 PG 트랙). 설계·합의·잔여 위험 = docs/plan-pg-beta-invite.md.
// allowlist 게이트는 App.tsx 마운트 지점(PG_BETA_INVITE_USER_IDS). 네트워크 조회 0, DB 0.

// 유저별 키 — 같은 브라우저에서 allowlist 계정 2개를 전환해도 한쪽 해제가 다른 쪽을 막지 않는다.
const STORAGE_KEY_PREFIX = "pg_beta_invite_dismissed:";
// 채널톡 wrapper의 isMobile(<= 768)과 같은 경계. 모바일은 채널톡이 로드되지 않아 CTA가 죽으므로 띄우지 않는다.
const MOBILE_MAX_WIDTH = 768;

// 키 값: ISO 시각까지 숨김(오늘은 그만 보기·초대 수락 → 다음날 0시). 기존 "true"(구 영구 해제)는 파싱 실패로 즉시 재노출.
// 영구 해제를 두지 않는 이유: 수락은 채널톡 프리필 열기 시도일 뿐 전송 보장이 아님 — 종료 신호는 allowlist 제거.
// localStorage 접근이 차단된 환경(프라이버시 모드 등)에서 effect/CTA가 죽지 않도록 가드(InventoryLaunchModal 동형).
function safeGetDismissed(userId: string): boolean {
  try {
    const value = localStorage.getItem(STORAGE_KEY_PREFIX + userId);
    if (value === null) return false;
    // 파싱 불가 값이면 NaN 비교 → false → 노출(닫을 길이 있는 쪽으로).
    return new Date() < new Date(value);
  } catch {
    // 저장소를 못 읽으면 노출하지 않는 쪽이 안전 — 해제도 저장 불가라 재노출 루프가 되기 때문.
    return true;
  }
}

// GA 이벤트(App.tsx의 gtag 패턴). 유저 연결은 App effect의 gtag('set', {user_id})가 담당 → GA 탐색(사용자 탐색기)에서 uid별로 view→accept/snooze/close 조회.
function gaEvent(name: string) {
  (window as { gtag?: (...args: unknown[]) => void }).gtag?.("event", name);
}

// 다음날 0시(로컬)까지 숨김. 오늘은 그만 보기·초대 수락 공통.
function hideUntilTomorrow(userId: string) {
  try {
    const until = new Date();
    until.setHours(24, 0, 0, 0);
    localStorage.setItem(STORAGE_KEY_PREFIX + userId, until.toISOString());
  } catch {
    // 저장 실패는 무시 — 이번 세션의 노출 억제는 isOpen state가 담당.
  }
}

export function PgBetaInviteModal() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  // 헤더(NewNavigationBar)와 같은 소스, 네트워크 조회 0. member.nickname과의 동기 검증은 플랜 §3.
  const nickname: string | undefined = user?.user_metadata?.nickname;
  const [isOpen, setIsOpen] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    // dep은 userId — user 객체 dep이면 토큰 리프레시마다 재실행되어 X로 닫은 뒤 같은 화면에서 재노출됨.
    // userId가 바뀌면 이전 계정의 열림 상태를 상속하지 않도록 먼저 닫는다.
    setIsOpen(false);
    if (!userId) return;
    if (safeGetDismissed(userId)) return;
    if (window.innerWidth <= MOBILE_MAX_WIDTH) return;
    setIsOpen(true);
  }, [userId]);

  // X / 바깥 클릭 / ESC = 현재 화면에서만 닫힘. 새로고침·재접속(재마운트) 시 다시 노출된다.
  const handleOpenChange = (open: boolean) => {
    if (!open) gaEvent("pg_beta_invite_close");
    setIsOpen(open);
  };

  const handleSnooze = () => {
    if (userId) hideUntilTomorrow(userId);
    gaEvent("pg_beta_invite_snooze");
    setIsOpen(false);
  };

  const handleApply = () => {
    // 클릭 이벤트 안에서 호출. false = 클릭 시점에 모바일 폭(창 축소 등) → 해제하지 않고 모달 유지.
    if (!openChatWithMessage("카드결제 베타 신청합니다")) return;
    // 전송 완료 이벤트가 SDK에 없어 "신청 완료"를 알 수 없다 — 스누즈와 동일하게 내일 재노출(미전송 셀러 재유도).
    if (userId) hideUntilTomorrow(userId);
    gaEvent("pg_beta_invite_accept");
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-lg"
        // 첫 포커스를 제목에 — 첫 버튼에 링이 잡히거나 하단 CTA로 스크롤되는 것 방지(InventoryLaunchModal 동형).
        // view는 여기서(열린 뒤 커밋) — 마운트 effect에서 쏘면 부모(App)의 gtag('set',{user_id}) effect보다 먼저라 uid 없이 나간다.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          titleRef.current?.focus();
          gaEvent("pg_beta_invite_view");
        }}
      >
        <DialogHeader className="space-y-3 text-left">
          <span className="w-fit rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">
            셀러헬퍼가 직접 선정한 5곳에만 드리는 초대
          </span>
          <DialogTitle
            ref={titleRef}
            tabIndex={-1}
            className="break-keep text-xl font-bold leading-snug text-slate-900 outline-none"
          >
            첫 카드결제 베타 셀러로 초대드립니다
          </DialogTitle>
          {/* asChild: Radix Description은 <p>라 내부 <div>가 invalid nesting — div로 대체 */}
          <DialogDescription asChild>
            <div className="space-y-4 pt-1 text-left text-[15px] leading-relaxed text-slate-600">
              <p className="break-keep">
                <strong className="font-semibold text-slate-900">
                  {nickname ? `${nickname}님은` : "셀러님은"} 첫 베타 셀러 5곳 중 한 곳으로 선정되었습니다.
                </strong>
              </p>
              <div className="space-y-0.5 break-keep">
                <p>"카드는 안 되나요?" 하고 돌아서던 고객을 이제 놓치지 않습니다.</p>
                <p>셀러헬퍼가 토스페이먼츠 공식 파트너가 되어 카드결제를 시작합니다.</p>
                <p>링크 기반으로 고객이 카드로 결제하면, 자동으로 '입금 완료' 처리가 가능합니다.</p>
              </div>
              <div className="space-y-0.5 break-keep">
                <p>
                  카드 수수료는 영세 1.2%부터 일반 2.7%까지,{" "}
                  <strong className="font-bold text-blue-600">국내 업계 최저 수준</strong>입니다.
                </p>
                <p>
                  <strong className="font-bold text-blue-600">
                    신청하시면 토스페이먼츠 담당자가 1:1로 배정되어 가입부터 사용까지 직접 안내합니다.
                  </strong>
                </p>
                <p>심사 서류 준비는 셀러헬퍼가 함께합니다.</p>
              </div>
              <div className="space-y-0.5 break-keep">
                <p>초대를 수락하시려면 아래 버튼을 눌러주세요.</p>
                <p>메시지를 전송하면 신청이 완료됩니다.</p>
                <p>전화 문의: 010-2988-3712</p>
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-2 flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            onClick={handleSnooze}
            className="h-auto self-center px-2 py-2 text-xs font-normal text-slate-500 hover:bg-transparent hover:text-slate-700 sm:self-auto"
          >
            오늘은 그만 보기
          </Button>
          <Button
            type="button"
            onClick={handleApply}
            className="w-full bg-slate-900 px-5 text-white hover:bg-slate-800 sm:w-auto"
          >
            초대 수락하기
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
